import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import HeroScene from '../components/3d/HeroScene';
import './Home.css';

const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-canvas">
          <HeroScene onBookClick={() => navigate('/books')} />
        </div>
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="hero-badge">&#8734; Infinity Presents</span>
          <h1 className="hero-title">
            Stories That<br />
            <span className="gradient-text">Break Barriers</span>
          </h1>
          <p className="hero-description">
            Explore the literary world of Kutloano Mkhulisi — where every word is a journey,
            every story a revelation, and every poem a window to the soul.
          </p>
          <div className="hero-actions">
            <Link to="/books" className="btn btn-primary">
              Explore Books
            </Link>
            <Link to="/about" className="btn btn-outline">
              Meet the Author
            </Link>
          </div>
        </motion.div>

        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="scroll-line" />
          <span>Scroll</span>
        </motion.div>
      </section>

      {/* Featured Book Section */}
      <section className="section featured-section">
        <div className="container">
          <motion.div
            className="featured-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="featured-image">
              <div className="book-cover-placeholder">
                <div className="book-spine" />
                <div className="book-front">
                  <span className="book-infinity">&#8734;</span>
                  <h3>Through the Fence</h3>
                  <p>Ano Mkhulisi</p>
                  <div className="book-ornament" />
                </div>
              </div>
            </div>
            <div className="featured-content">
              <span className="featured-label">Featured Book</span>
              <h2 className="section-title">Through the Fence</h2>
              <p className="featured-desc">
                A captivating story about Dahlia, a young African girl born with striking blue eyes
                in a secluded community surrounded by an imposing fence. As she navigates a world
                that both fears and reveres her uniqueness, she discovers that the barriers keeping
                her community isolated hold secrets that could change everything.
              </p>
              <div className="featured-meta">
                <div className="meta-item">
                  <span className="meta-label">Genre</span>
                  <span className="meta-value">Fiction</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Chapters</span>
                  <span className="meta-value">8</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Year</span>
                  <span className="meta-value">2024</span>
                </div>
              </div>
              <Link to="/books" className="btn btn-primary">
                Read Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections Preview */}
      <section className="section content-preview">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Explore the Collection</h2>
            <p className="section-subtitle">
              Dive into a world of stories, poetry, and imagination
            </p>
          </motion.div>

          <div className="preview-grid">
            <motion.div
              className="preview-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8 }}
            >
              <Link to="/books">
                <div className="preview-icon">&#128214;</div>
                <h3>Books</h3>
                <p>Full-length novels and stories that transport you to new worlds</p>
                <span className="preview-link">Explore &rarr;</span>
              </Link>
            </motion.div>

            <motion.div
              className="preview-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8 }}
            >
              <Link to="/poems">
                <div className="preview-icon">&#9998;</div>
                <h3>Poetry</h3>
                <p>Verses that capture the essence of the human experience</p>
                <span className="preview-link">Read &rarr;</span>
              </Link>
            </motion.div>

            <motion.div
              className="preview-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -8 }}
            >
              <Link to="/stories">
                <div className="preview-icon">&#128220;</div>
                <h3>Short Stories</h3>
                <p>Brief but powerful tales that linger long after reading</p>
                <span className="preview-link">Discover &rarr;</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="container">
          <motion.div
            className="quote-block"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <span className="quote-mark">&ldquo;</span>
            <p className="quote-text">
              Writing is how I make sense of the world. Every story is a piece of my soul,
              offered to anyone brave enough to turn the page.
            </p>
            <span className="quote-author">— Kutloano Mkhulisi</span>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
